import { Fragment } from "react";

/**
 * Names that must survive machine translation letter for letter.
 *
 * Longest first — the matcher takes the first alternative that fits, so a
 * shorter name that is a prefix of a longer one would otherwise win and leave
 * the tail to be translated.
 */
const BRAND_TERMS = ["Kaympus", "Kayease"];

const BRAND_PATTERN = new RegExp(
  `(${[...BRAND_TERMS].sort((a, b) => b.length - a.length).join("|")})`,
  "gi"
);

/**
 * Renders text that the Google widget IS allowed to translate, while holding
 * the brand names out of it.
 *
 * Needed only inside `translate="yes"` regions — copy that came from the CMS
 * or the subscriptions API and therefore has no dictionary entry. Everything
 * we wrote ourselves sits under the `notranslate` on `.landing-root` and never
 * reaches the widget at all.
 *
 * Without this, a plan literally called "Kaympus Starter" comes back as
 * "केम्पस स्टार्टर" on one card and "कायम्पस ग्रोथ" on the next — the widget
 * transliterates phonetically and is not consistent between runs, so the
 * product ends up with three spellings of its own name on one page. Worse,
 * कैम्पस is the ordinary Hindi word for *campus*, so the brand doesn't just
 * change spelling, it stops reading as a brand.
 *
 * Splitting the string and marking only the brand runs keeps the rest
 * translatable, which is the whole point of those regions.
 */
export default function BrandSafeText({ children }: { children?: string | null }) {
  if (!children) return null;

  const parts = children.split(BRAND_PATTERN);

  return (
    <>
      {parts.map((part, i) =>
        BRAND_PATTERN.test(part) && BRAND_TERMS.some((b) => b.toLowerCase() === part.toLowerCase()) ? (
          <span key={i} translate="no" className="notranslate">
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  );
}
